import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McPagination, McTooltip } from "@maersk-global/mds-react-wrapper";
import ApplicationFormFields from "../../RedGreenChannelTracker/ApplicationFormFields";
import StatisticsModal from "../../RedGreenChannelTracker/StatisticsModal";
import DocumentUploadModal from "../../RedGreenChannelTracker/DocumentUploadModal";
import {
  fetchAppDetailsByAppId,
  fetchAppDetailsByAppName,
  fetchIncidentDetails,
  updateApplication,
  validateMandatoryFields,
  fetchStatisticsData,
} from "../../RedGreenChannelTracker/apiHelpers";

const RECORDS_PER_PAGE = 25;

const normalizeAddedToRedChannelForStats = (dateValue) => {
  if (!dateValue) return "";

  const raw = String(dateValue).trim();
  if (!raw || raw === "-") return "";

  const isoDate = DateTime.fromISO(raw);
  if (isoDate.isValid) {
    return isoDate.toFormat("dd-MM-yyyy");
  }

  const ddMmYyyyDate = DateTime.fromFormat(raw, "dd-MM-yyyy");
  if (ddMmYyyyDate.isValid) {
    return ddMmYyyyDate.toFormat("dd-MM-yyyy");
  }

  const yyyyMmDdDate = DateTime.fromFormat(raw, "yyyy-MM-dd");
  if (yyyyMmDdDate.isValid) {
    return yyyyMmDdDate.toFormat("dd-MM-yyyy");
  }

  return raw;
};

export default function RedChannelNonCompliantSection({
  tokenFetched,
  userRole,
  userEmail,
}) {
  const isChangeManagerUser =
    String(userRole || "").trim().toLowerCase() === "change manager";

  const [nonCompliantApps, setNonCompliantApps] = useState([]);
  const [nonCompliantLoading, setNonCompliantLoading] = useState(true);
  const [nonCompliantSortColumn, setNonCompliantSortColumn] = useState(
    "added_to_red_channel",
  );
  const [nonCompliantSortDirection, setNonCompliantSortDirection] =
    useState("ascending");
  const [nonCompliantCurrentPage, setNonCompliantCurrentPage] = useState(1);

  const [isStatisticsModalOpen, setIsStatisticsModalOpen] = useState(false);
  const [statisticsData, setStatisticsData] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [postmortemPreventiveMeasures, setPostmortemPreventiveMeasures] =
    useState([]);
  const [selectedRowForStats, setSelectedRowForStats] = useState(null);
  const [incidentCounts, setIncidentCounts] = useState(null);
  const [oldestIncidentDate, setOldestIncidentDate] = useState(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [originalAppName, setOriginalAppName] = useState("");
  const [editValidationError, setEditValidationError] = useState("");
  const [editFormData, setEditFormData] = useState({});
  const [editAppNameOptions, setEditAppNameOptions] = useState([]);
  const editAppNameTypeaheadRef = useRef(null);
  const editAppIdTimerRef = useRef(null);
  const editAppNameTimerRef = useRef(null);
  const editIncidentTimerRef = useRef(null);

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    recipients: "",
    subject: "",
    body: "",
    includeAttachment: true,
  });
  const [selectedRowForEmail, setSelectedRowForEmail] = useState(null);
  const [isLoadingOwners, setIsLoadingOwners] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const [isDocumentUploadModalOpen, setIsDocumentUploadModalOpen] =
    useState(false);
  const [selectedRowForUpload, setSelectedRowForUpload] = useState(null);

  const fetchNonCompliantApps = async () => {
    if (!tokenFetched) {
      return;
    }

    setNonCompliantLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URI}/api/redGreenChannelNonCompliant`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token") || ""}`,
          },
        },
      );

      const result = Array.isArray(response?.data) ? response.data : [];
      setNonCompliantApps(result);
    } catch (error) {
      setNonCompliantApps([]);
      toast.error("Failed to load red channel non-compliant apps", {
        position: "top-center",
        autoClose: 3000,
        theme: "light",
      });
    } finally {
      setNonCompliantLoading(false);
    }
  };

  useEffect(() => {
    if (!tokenFetched) {
      return;
    }

    fetchNonCompliantApps();
  }, [tokenFetched]);

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const nonCompliantColumns = [
    { id: "edit_action", label: "Actions", sortable: false },
    { id: "app_id", label: "App ID", sortable: true },
    { id: "app_name", label: "App Instance Name", sortable: true },
    { id: "platform", label: "Platform", sortable: true },
    { id: "portfolio", label: "Portfolio", sortable: true },
    { id: "status", label: "Status", sortable: true },
    { id: "added_to_red_channel", label: "Added to Red Channel", sortable: true },
    { id: "trigger_type", label: "Trigger", sortable: true },
    { id: "latest_incident", label: "Latest Incident", sortable: true },
    { id: "priority", label: "Priority", sortable: true },
    { id: "latest_incident_date", label: "Latest Incident Date", sortable: true },
    { id: "final_review", label: "Final Review", sortable: true },
    { id: "deployment_type", label: "Deployment Type", sortable: true },
    { id: "deployment_details", label: "Deployment Details", sortable: true },
  ];

  const nonCompliantTableData = useMemo(() => {
    return (nonCompliantApps || []).map((item, index) => ({
      id: `non-compliant-${item.app_id || "na"}-${item.latest_incident || "no-incident"}-${index}`,
      app_id: item.app_id || "-",
      app_name: item.app_name || "-",
      platform: item.platform || "-",
      portfolio: item.portfolio || "-",
      status: item.status || "-",
      trigger_type: item.trigger_type || "-",
      latest_incident: item.latest_incident || "-",
      priority: item.priority || "-",
      latest_incident_date: item.latest_incident_date || "-",
      added_to_red_channel: formatDateForDisplay(item.added_to_red_channel),
      final_review: formatDateForDisplay(item.final_review),
      deployment_type: item.deployment_type || "-",
      deployment_details: item.deployment_details || "-",
      comments: item.comments || "-",
      _originalData: item,
    }));
  }, [nonCompliantApps]);

  const sortedNonCompliantTableData = useMemo(() => {
    const dateColumns = ["added_to_red_channel", "final_review", "latest_incident_date"];
    const directionMultiplier = nonCompliantSortDirection === "ascending" ? 1 : -1;

    return [...nonCompliantTableData].sort((a, b) => {
      let leftValue = a[nonCompliantSortColumn];
      let rightValue = b[nonCompliantSortColumn];

      if (dateColumns.includes(nonCompliantSortColumn)) {
        const parseDateValue = (value) => {
          if (!value || value === "-") return null;
          if (typeof value === "string" && value.includes("-")) {
            const [day, month, year] = value.split("-");
            const parsed = new Date(Number(year), Number(month) - 1, Number(day));
            return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
          }
          const parsed = new Date(value);
          return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
        };

        const leftDate = parseDateValue(leftValue);
        const rightDate = parseDateValue(rightValue);

        if (leftDate === rightDate) return 0;
        if (leftDate === null) return 1;
        if (rightDate === null) return -1;
        return leftDate > rightDate ? directionMultiplier : -1 * directionMultiplier;
      }

      leftValue = String(leftValue || "").toLowerCase();
      rightValue = String(rightValue || "").toLowerCase();

      if (leftValue === rightValue) return 0;
      return leftValue > rightValue ? directionMultiplier : -1 * directionMultiplier;
    });
  }, [nonCompliantTableData, nonCompliantSortColumn, nonCompliantSortDirection]);

  const nonCompliantTotalPages = Math.max(
    1,
    Math.ceil(sortedNonCompliantTableData.length / RECORDS_PER_PAGE),
  );

  const paginatedNonCompliantTableData = useMemo(() => {
    const startIndex = (nonCompliantCurrentPage - 1) * RECORDS_PER_PAGE;
    return sortedNonCompliantTableData.slice(
      startIndex,
      startIndex + RECORDS_PER_PAGE,
    );
  }, [sortedNonCompliantTableData, nonCompliantCurrentPage]);

  const handleNonCompliantSortChange = ({ detail: { column, direction } }) => {
    setNonCompliantSortColumn(column);
    setNonCompliantSortDirection(direction);
  };

  const handleNonCompliantPageChange = (event) => {
    setNonCompliantCurrentPage(event.detail);
  };

  useEffect(() => {
    setNonCompliantCurrentPage(1);
  }, [nonCompliantSortColumn, nonCompliantSortDirection]);

  const handleOpenStatisticsModal = async (row) => {
    setSelectedRowForStats(row);
    setIsStatisticsModalOpen(true);
    setStatisticsLoading(true);

    try {
      const addedToRedChannelParam = normalizeAddedToRedChannelForStats(
        row._originalData?.added_to_red_channel || row.added_to_red_channel,
      );

      const {
        statisticsData: stats,
        incidentCounts: counts,
        postmortemPreventiveMeasures: measures,
        oldestIncidentDate: oldestDate,
      } = await fetchStatisticsData(
        row.app_id,
        row.app_name,
        addedToRedChannelParam,
        row.latest_incident,
      );

      setStatisticsData(stats);
      setIncidentCounts(counts);
      setPostmortemPreventiveMeasures(measures || []);
      setOldestIncidentDate(oldestDate || null);
    } catch (error) {
      setStatisticsData(null);
      setIncidentCounts(null);
      setPostmortemPreventiveMeasures([]);
      setOldestIncidentDate(null);
    } finally {
      setStatisticsLoading(false);
    }
  };

  const handleCloseStatisticsModal = () => {
    setIsStatisticsModalOpen(false);
    setSelectedRowForStats(null);
    setStatisticsData(null);
    setIncidentCounts(null);
    setPostmortemPreventiveMeasures([]);
    setOldestIncidentDate(null);
  };

  const handleOpenEditModal = (rowData) => {
    const originalData = rowData._originalData || {};
    setOriginalAppName(originalData.app_name || "");
    setEditValidationError("");
    setEditFormData({
      app_id: originalData.app_id || "",
      app_name: originalData.app_name || "",
      platform: originalData.platform || "",
      portfolio: originalData.portfolio || "",
      status: originalData.status || "Red",
      trigger_type: originalData.trigger_type || "",
      incident: originalData.incident || "",
      priority: originalData.priority || "",
      incident_dates: originalData.incident_dates || "",
      added_to_red_channel: originalData.added_to_red_channel
        ? DateTime.fromISO(originalData.added_to_red_channel)
        : DateTime.now(),
      moved_to_green_channel_on: originalData.moved_to_green_channel_on
        ? DateTime.fromISO(originalData.moved_to_green_channel_on)
        : null,
      final_review: originalData.final_review
        ? DateTime.fromISO(originalData.final_review)
        : DateTime.now().plus({ months: 3 }),
      deployment_type: originalData.deployment_type || "",
      deployment_details: originalData.deployment_details || "",
      comments: originalData.comments || "",
    });
    setEditAppNameOptions([]);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditValidationError("");
  };

  const handleEditFormChange = (field, value) => {
    if (field === "added_to_red_channel" && value) {
      const addedDate = DateTime.isDateTime(value)
        ? value
        : DateTime.fromJSDate(value);
      setEditFormData((prev) => ({
        ...prev,
        added_to_red_channel: addedDate,
        final_review: addedDate.plus({ months: 3 }),
      }));
      return;
    }

    if (field === "status") {
      if (value === "Green") {
        setEditFormData((prev) => ({
          ...prev,
          status: value,
          moved_to_green_channel_on:
            prev.moved_to_green_channel_on || DateTime.now(),
        }));
      } else {
        setEditFormData((prev) => ({
          ...prev,
          status: value,
          moved_to_green_channel_on: null,
        }));
      }
      return;
    }

    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditAppIdInput = (value) => {
    handleEditFormChange("app_id", value);
    if (editAppIdTimerRef.current) clearTimeout(editAppIdTimerRef.current);
    editAppIdTimerRef.current = setTimeout(async () => {
      if (!value) {
        return;
      }
      const data = await fetchAppDetailsByAppId(value);
      if (data) {
        setEditFormData((prev) => ({
          ...prev,
          platform: data.platform || prev.platform,
          portfolio: data.portfolio || prev.portfolio,
        }));
        if (data.app_names && data.app_names.length > 0) {
          setEditAppNameOptions(
            data.app_names.map((name) => ({ label: name, value: name })),
          );
        }
      }
    }, 800);
  };

  const handleEditAppNameInput = (value) => {
    handleEditFormChange("app_name", value);
    if (editAppNameTimerRef.current) clearTimeout(editAppNameTimerRef.current);
    editAppNameTimerRef.current = setTimeout(async () => {
      if (!value) {
        return;
      }
      const data = await fetchAppDetailsByAppName(value);
      if (data) {
        setEditFormData((prev) => ({
          ...prev,
          app_id: data.app_id || prev.app_id,
          platform: data.platform || prev.platform,
          portfolio: data.portfolio || prev.portfolio,
        }));
      }
    }, 800);
  };

  const handleEditAppNameSelected = (event) => {
    const selectedName = event.detail?.label || event.detail;
    setEditFormData((prev) => ({ ...prev, app_name: selectedName }));
  };

  const handleEditIncidentInput = (value) => {
    handleEditFormChange("incident", value);
    if (editIncidentTimerRef.current) clearTimeout(editIncidentTimerRef.current);
    editIncidentTimerRef.current = setTimeout(async () => {
      if (!value) {
        return;
      }
      const data = await fetchIncidentDetails(value);
      if (data) {
        setEditFormData((prev) => ({
          ...prev,
          priority: data.priority || prev.priority,
          incident_dates: data.incident_date || prev.incident_dates,
        }));
      }
    }, 800);
  };

  const handleSubmitEditApplication = async () => {
    const missingFields = validateMandatoryFields(editFormData, true);
    if (missingFields.length > 0) {
      setEditValidationError(missingFields.join(", "));
      return;
    }

    setIsEditSubmitting(true);
    setEditValidationError("");

    try {
      await updateApplication(editFormData, userEmail, originalAppName);
      toast.success("Application updated successfully!", {
        position: "top-center",
        autoClose: 3000,
      });
      setIsEditModalOpen(false);
      await fetchNonCompliantApps();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update application", {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleOpenDocumentUploadModal = (row) => {
    setSelectedRowForUpload(row);
    setIsDocumentUploadModalOpen(true);
  };

  const handleCloseDocumentUploadModal = () => {
    setIsDocumentUploadModalOpen(false);
    setSelectedRowForUpload(null);
  };

  const handleDocumentUploadSuccess = () => {
    fetchNonCompliantApps();
  };

  const handleOpenEmailModal = async (row) => {
    setSelectedRowForEmail(row);
    setIsEmailModalOpen(true);
    setIsLoadingOwners(true);

    try {
      let recipients = "";
      let incidentShortDesc = "";
      let incidentCIName = "";

      if (row.latest_incident && row.latest_incident !== "-") {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URI}/api/redChannelEmailDetails?app_name=${encodeURIComponent(row.app_name)}&incident_id=${encodeURIComponent(row.latest_incident)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
            },
          },
        );

        if (response.data && response.data.status === "success") {
          recipients = response.data.app_owners
            ? response.data.app_owners.join(", ")
            : "";
          if (response.data.incident_details) {
            incidentShortDesc =
              response.data.incident_details.short_description || "";
            incidentCIName =
              response.data.incident_details.configuration_item || "";
          }
        }
      }

      const deploymentType = row.deployment_type || "";
      const isManualDeployment = deploymentType.toLowerCase() === "manual";
      const addedDate = row._originalData?.added_to_red_channel
        ? DateTime.fromISO(row._originalData.added_to_red_channel).toFormat(
            "dd-MMM-yyyy",
          )
        : DateTime.now().toFormat("dd-MMM-yyyy");

      let subject = "";
      let body = "";

      if (isManualDeployment) {
        subject = `Red Channel Onboarding - ${row.app_id} - ${row.app_name}`;
        body = `Hello,\n\nAs part of Red-Green Channel Framework, we are writing to inform you that the app ${row.app_id}-${row.app_name} have been onboarded to the Red Channel.\n\nRed Channel Tracker: https://flux.maersk.io/RedGreenChannelTracker\n\nReason for Red Channel Placement:\n• Major Incident ${row.latest_incident || ""}: ${incidentShortDesc || ""}. CI Name: ${incidentCIName || ""}\n\nBest Regards,\nTechnology Change Management Team`;
      } else {
        subject = `Red Channel Placement - ${row.app_id} – ${row.app_name}`;
        body = `Hello,\n\nAs part of our Red-Green Channel Framework to ensure safe and reliable production changes/deployments, your application '${row.app_id} – ${row.app_name}' has been placed in the Red Channel effective ${addedDate}.\n\nReason for Red Channel Placement:\n• Major Incident ${row.latest_incident || ""}: ${incidentShortDesc || ""}. CI Name: ${incidentCIName || ""}\n\nBest Regards,\nTechnology Change Management Team`;
      }

      setEmailFormData({
        recipients,
        subject,
        body,
        includeAttachment: true,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load email details", {
        position: "top-right",
        autoClose: 3000,
      });
      setEmailFormData({
        recipients: "",
        subject: "",
        body: "",
        includeAttachment: true,
      });
    } finally {
      setIsLoadingOwners(false);
    }
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setSelectedRowForEmail(null);
    setEmailFormData({
      recipients: "",
      subject: "",
      body: "",
      includeAttachment: true,
    });
  };

  const handleEmailFormChange = (field, value) => {
    setEmailFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleSendEmail = async () => {
    const recipientsArray = emailFormData.recipients
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    if (recipientsArray.length === 0) {
      toast.error("Please enter at least one recipient email address", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const invalidEmails = recipientsArray.filter((email) => !validateEmail(email));
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email format: ${invalidEmails.join(", ")}`, {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }

    const trimmedBody = emailFormData.body.trim();
    if (!trimmedBody) {
      toast.error("Email body cannot be empty", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsSendingEmail(true);
    try {
      let htmlBody = trimmedBody.replace(/\n/g, "<br>");
      htmlBody = htmlBody.replace(
        "Red Channel Tracker: https://flux.maersk.io/RedGreenChannelTracker",
        'Red Channel Tracker: <a href="https://flux.maersk.io/RedGreenChannelTracker">https://flux.maersk.io/RedGreenChannelTracker</a>',
      );
      const htmlContent = `<html><body style="font-family: Arial, sans-serif;">${htmlBody}</body></html>`;

      const requestBody = {
        recipients: recipientsArray,
        subject: emailFormData.subject,
        content: htmlContent,
      };

      const isAutomatedDeployment =
        selectedRowForEmail?.deployment_type?.toLowerCase() === "automated";
      if (emailFormData.includeAttachment && isAutomatedDeployment) {
        requestBody.includeAttachment = true;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/api/sendEmail`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data && response.data.success) {
        toast.success(response.data.message || "Email sent successfully", {
          position: "top-right",
          autoClose: 3000,
        });
        handleCloseEmailModal();
      } else {
        toast.error(response.data?.message || "Failed to send email", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send email", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <>
      <div className="dashboard-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <h3 style={{ margin: 0, fontWeight: 500 }}>
            Red Channel Non-Compliant Apps
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "0.875rem", color: "#d32f2f", fontWeight: 600 }}>
              Total Records: {nonCompliantTableData.length}
            </span>
            <McButton
              appearance="neutral"
              variant="outlined"
              fit="small"
              click={fetchNonCompliantApps}
              disabled={nonCompliantLoading}
              icon="arrow-clockwise"
            >
              Refresh
            </McButton>
          </div>
        </div>

        <McTable
          data={paginatedNonCompliantTableData}
          columns={nonCompliantColumns}
          datakey="id"
          fit="small"
          headersticky
          sortmanual
          sortchange={handleNonCompliantSortChange}
          sortdefaultcolumnid={nonCompliantSortColumn}
          sortdefaultdirection={nonCompliantSortDirection}
          customstyles={`
            .mds-table {
              max-height: 55vh !important;
            }
          `}
        >
          {nonCompliantLoading ? (
            <mc-loading-indicator slot="state"></mc-loading-indicator>
          ) : (
            paginatedNonCompliantTableData.length === 0 && (
              <div slot="state" className="empty-row">
                No non-compliant apps found.
              </div>
            )
          )}

          {paginatedNonCompliantTableData.map((row) => (
            <Fragment key={row.id}>
              <div slot={`${row.id}_edit_action`}>
                <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                  <McTooltip opendelay={50} appearance="neutral-default" trigger="hover focus">
                    <McButton
                      slot="trigger"
                      fit="small"
                      click={() => handleOpenStatisticsModal(row)}
                      icon="file-eye"
                      hiddenlabel={true}
                      appearance="neutral"
                      variant="outlined"
                    />
                    <span>Additional Details & Documents</span>
                  </McTooltip>

                  {isChangeManagerUser && (
                    <McTooltip opendelay={50} appearance="neutral-default" trigger="hover focus">
                      <McButton
                        slot="trigger"
                        fit="small"
                        click={() => handleOpenEditModal(row)}
                        icon="pencil"
                        hiddenlabel={true}
                        appearance="neutral"
                        variant="outlined"
                      />
                      <span>Edit Details</span>
                    </McTooltip>
                  )}

                  {isChangeManagerUser && (
                    <McTooltip opendelay={50} appearance="neutral-default" trigger="hover focus">
                      <McButton
                        slot="trigger"
                        fit="small"
                        click={() => handleOpenDocumentUploadModal(row)}
                        icon="tray-arrow-up"
                        hiddenlabel={true}
                        appearance="neutral"
                        variant="outlined"
                      />
                      <span>Upload Document</span>
                    </McTooltip>
                  )}

                  {isChangeManagerUser && (
                    <McTooltip opendelay={50} appearance="neutral-default" trigger="hover focus">
                      <McButton
                        slot="trigger"
                        fit="small"
                        click={() => handleOpenEmailModal(row)}
                        icon="bell"
                        hiddenlabel={true}
                        appearance="neutral"
                        variant="outlined"
                      />
                      <span>Send Email Notification</span>
                    </McTooltip>
                  )}
                </div>
              </div>

              <div slot={`${row.id}_app_id`}>
                {row.app_id && row.app_id !== "-" ? (
                  <a
                    href={`https://msa.maersk.io/asset_lookup?applicationId=${encodeURIComponent(row.app_id)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#0077b2",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    {row.app_id}
                  </a>
                ) : (
                  <span>{row.app_id}</span>
                )}
              </div>

              <div slot={`${row.id}_latest_incident`}>
                {row.latest_incident && row.latest_incident !== "-" ? (
                  <a
                    href={`https://maersk.service-now.com/now/nav/ui/classic/params/target/incident.do%3Fsysparm_query%3Dnumber%3D${row.latest_incident}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#0077b2",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    {row.latest_incident}
                  </a>
                ) : (
                  <span>{row.latest_incident}</span>
                )}
              </div>

              <div slot={`${row.id}_status`}>
                <mc-tag appearance={row.status === "Red" ? "error" : "success"} fit="small">
                  {row.status}
                </mc-tag>
              </div>
            </Fragment>
          ))}
        </McTable>

        {!nonCompliantLoading && nonCompliantTotalPages > 1 && (
          <div
            style={{
              paddingTop: "1rem",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <McPagination
              currentpage={nonCompliantCurrentPage}
              totalpages={nonCompliantTotalPages}
              pagechange={handleNonCompliantPageChange}
            />
          </div>
        )}
      </div>

      <McModal
        heading="Edit Record"
        dimension="large"
        padding="default"
        open={isEditModalOpen}
        closed={handleCloseEditModal}
        closing={handleCloseEditModal}
        zindex={999}
      >
        <ApplicationFormFields
          formData={editFormData}
          appNameOptions={editAppNameOptions}
          appNameTypeaheadRef={editAppNameTypeaheadRef}
          onAppIdInput={handleEditAppIdInput}
          onAppNameInput={handleEditAppNameInput}
          onAppNameSelected={handleEditAppNameSelected}
          onIncidentInput={handleEditIncidentInput}
          onFormChange={handleEditFormChange}
          isEditMode={true}
        />
        {editValidationError && (
          <div
            style={{
              padding: "12px",
              marginTop: "16px",
              marginBottom: "16px",
              backgroundColor: "#fef0f0",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              color: "#721c24",
            }}
          >
            <strong>Please fill in the mandatory fields:</strong>
            <div style={{ marginTop: "4px" }}>{editValidationError}</div>
          </div>
        )}
        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          fit="medium"
          disabled={isEditSubmitting}
          click={handleCloseEditModal}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="primary"
          fit="medium"
          disabled={isEditSubmitting}
          loading={isEditSubmitting}
          click={handleSubmitEditApplication}
        >
          Save
        </McButton>
      </McModal>

      <StatisticsModal
        isOpen={isStatisticsModalOpen}
        loading={statisticsLoading}
        selectedRow={selectedRowForStats}
        statisticsData={statisticsData}
        incidentCounts={incidentCounts}
        oldestIncidentDate={oldestIncidentDate}
        postmortemPreventiveMeasures={postmortemPreventiveMeasures}
        onClose={handleCloseStatisticsModal}
        userRole={userRole}
        userEmail={userEmail}
      />

      <DocumentUploadModal
        isOpen={isDocumentUploadModalOpen}
        appName={selectedRowForUpload?.app_name}
        recordStatus={selectedRowForUpload?.status}
        userEmail={userEmail}
        onClose={handleCloseDocumentUploadModal}
        onUploadSuccess={handleDocumentUploadSuccess}
      />

      <McModal
        heading="Send Email Notification"
        dimension="large"
        padding="default"
        open={isEmailModalOpen}
        closed={handleCloseEmailModal}
        closing={handleCloseEmailModal}
        zindex={999}
      >
        <div style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" }}>
          {isLoadingOwners ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <mc-loading-indicator></mc-loading-indicator>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                paddingBottom: "1rem",
                paddingRight: "0.5rem",
              }}
            >
              {selectedRowForEmail && (
                <div
                  style={{ padding: "0.75rem", backgroundColor: "#f5f5f5", borderRadius: "4px" }}
                >
                  <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>
                    <strong>App Name:</strong> {selectedRowForEmail.app_name}
                  </p>
                  <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>
                    <strong>App ID:</strong> {selectedRowForEmail.app_id}
                  </p>
                  <p style={{ margin: "0.25rem 0", fontSize: "0.875rem" }}>
                    <strong>Platform:</strong> {selectedRowForEmail.platform}
                  </p>
                </div>
              )}

              {selectedRowForEmail &&
                selectedRowForEmail.deployment_type &&
                selectedRowForEmail.deployment_type.toLowerCase() === "automated" && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginTop: "0.5rem",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="includeAttachment"
                      checked={emailFormData.includeAttachment}
                      onChange={(event) =>
                        handleEmailFormChange("includeAttachment", event.target.checked)
                      }
                      style={{ cursor: "pointer" }}
                    />
                    <label
                      htmlFor="includeAttachment"
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#333",
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      Include process communication attachment
                    </label>
                  </div>
                )}

              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label style={{ fontSize: "0.875rem", fontWeight: "600", color: "#333" }}>
                  Recipients (comma-separated emails)
                </label>
                <textarea
                  value={emailFormData.recipients}
                  onChange={(event) =>
                    handleEmailFormChange("recipients", event.target.value)
                  }
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    fontSize: "0.875rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                    resize: "vertical",
                  }}
                  placeholder="Enter email addresses separated by commas"
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label style={{ fontSize: "0.875rem", fontWeight: "600", color: "#333" }}>
                  Subject
                </label>
                <input
                  type="text"
                  value={emailFormData.subject}
                  onChange={(event) =>
                    handleEmailFormChange("subject", event.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    fontSize: "0.875rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                  }}
                  placeholder="Enter email subject"
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label style={{ fontSize: "0.875rem", fontWeight: "600", color: "#333" }}>
                  Body
                </label>
                <textarea
                  value={emailFormData.body}
                  onChange={(event) =>
                    handleEmailFormChange("body", event.target.value)
                  }
                  rows={18}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    fontSize: "0.875rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                    resize: "vertical",
                    whiteSpace: "pre-wrap",
                    minHeight: "350px",
                  }}
                  placeholder="Enter email message"
                />
              </div>
            </div>
          )}
        </div>

        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="outlined"
          dialogaction="cancel"
          fit="medium"
          click={handleCloseEmailModal}
          disabled={isLoadingOwners || isSendingEmail}
        >
          Cancel
        </McButton>
        <McButton
          slot="primaryAction"
          appearance="primary"
          fit="medium"
          click={handleSendEmail}
          disabled={
            isLoadingOwners ||
            isSendingEmail ||
            !emailFormData.recipients ||
            !emailFormData.subject ||
            !emailFormData.body
          }
          loading={isSendingEmail}
        >
          Send Email
        </McButton>
      </McModal>
    </>
  );
}
